import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useFlow } from 'context/FlowContext';
import { submitGuestCheckout, checkDeliveryAvailability, previewDeliveryFeeWaivers } from 'services/publicFlowService';
import { summarizeSelectedIngredients } from 'utils/ingredientSummary';
import { getLineUnitPrice } from 'utils/cartPricing';
import { groupCartLinesBySignature } from 'utils/cartSignature';
import { memberDisplayName } from 'utils/household';
import { isValidPhone } from 'utils/phoneFormat';
import { useDeliveryPrefill } from 'hooks/useDeliveryPrefill';
import DeliveryAddressFields from 'components/shared/DeliveryAddressFields';
import DeliverySlotSelect from 'components/shared/DeliverySlotSelect';
import { CARD_FEE_RATE } from 'constants/pricing';
import { formatTaxRatePercent } from 'utils/tax';
import {
  cartDeliveryFee,
  groupCartByDelivery,
  preferredSlot,
  preferredSlotKey,
  slotKey,
  sortSlots,
} from 'utils/deliverySlots';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const {
    cart,
    cartSubtotal,
    householdMembers,
    delivery,
    setDelivery,
    entryFlow,
    primaryHasFullQuiz,
    setLastOrder,
    removeFromCart,
    setCartLineQuantity,
    setCartLineDeliverySlot,
    setCartDeliverySlots,
    splitCartLineServing,
  } = useFlow();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Default: one delivery slot for the whole order. Checking this reveals a
  // per-meal picker on every line instead.
  const [splitDelivery, setSplitDelivery] = useState(false);
  // state: 'idle' | 'checking' | 'supported' | 'unsupported' | 'unknown' (backend unreachable — don't block)
  const [zipStatus, setZipStatus] = useState({ state: 'idle', taxRate: null, message: '', slots: [] });

  // Fills address/phone/notes from the signed-in customer's saved profile
  // address, blank fields only — a no-op for guest shoppers.
  useDeliveryPrefill(delivery, setDelivery);

  // Menu-page cards always add a separate cart line per serving. Here in the
  // cart, servings that are identical (same dish, portion and every selected
  // ingredient) are shown consolidated as one row with a combined quantity,
  // rather than as repeated rows (Claire, Jul 2026; see utils/cartSignature.js).
  // The underlying lines stay separate — only the display groups them.
  // The delivery slot is part of that identity here: identical servings sent on
  // different days are two rows, each with its own picker.
  const cartByMember = useMemo(() => {
    const groups = {};
    householdMembers.forEach((m) => {
      groups[m.id] = { member: m, lines: [] };
    });
    cart.forEach((line) => {
      if (groups[line.memberId]) {
        groups[line.memberId].lines.push(line);
      }
    });
    return Object.values(groups)
      .filter((g) => g.lines.length > 0)
      .map((g) => ({
        ...g,
        groupedLines: groupCartLinesBySignature(g.lines, { includeDeliverySlot: true }),
      }));
  }, [cart, householdMembers]);

  useEffect(() => {
    const zip = String(delivery.zip || '').replace(/\D/g, '');
    if (zip.length < 5) {
      setZipStatus({ state: 'idle', taxRate: null, message: '', slots: [] });
      return undefined;
    }
    let cancelled = false;
    setZipStatus((prev) => ({ ...prev, state: 'checking' }));
    const timer = setTimeout(() => {
      checkDeliveryAvailability(zip).then((result) => {
        if (cancelled) return;
        if (!result) {
          setZipStatus({ state: 'unknown', taxRate: null, message: '', slots: [] });
        } else if (result.supported) {
          setZipStatus({ state: 'supported', taxRate: result.taxRate, message: '', slots: result.slots || [] });
        } else {
          setZipStatus({ state: 'unsupported', taxRate: null, message: result.message, slots: [] });
        }
      });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [delivery.zip]);

  // Every slot the zip supports, already cutoff-filtered and date-resolved
  // server-side (/api/delivery-availability/:zip); just sorted here.
  const sortedSlots = useMemo(() => sortSlots(zipStatus.slots), [zipStatus.slots]);

  // On a fresh slot list, meals with no slot — or one this area doesn't deliver
  // — fall back to the preferred slot (Sunday evening where supported, else the
  // earliest). Still-valid slots are kept: silently rewriting an arrangement the
  // customer built by hand is worse than a stale-but-deliverable choice. The
  // order-level day/time remains the default new meals inherit and the backend's
  // fallback for an older saved cart.
  useEffect(() => {
    if (!sortedSlots.length) return;
    const preferred = preferredSlot(sortedSlots);
    setDelivery({ deliveryDay: preferred.day, deliveryTime: preferred.time });
    setCartDeliverySlots(preferred, { validSlots: sortedSlots });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedSlots]);

  // Informational only — keyed like `deliveryGroups` below. Checkout re-checks
  // the waiver authoritatively server-side regardless of what's shown here.
  const [feePreview, setFeePreview] = useState({});
  useEffect(() => {
    if (zipStatus.state !== 'supported' || !String(delivery.address || '').trim() || !cart.length) {
      setFeePreview({});
      return undefined;
    }
    const slots = groupCartByDelivery(cart, zipStatus.slots, delivery)
      .filter((g) => g.day && g.time)
      .map((g) => ({ day: g.day, time: g.time }));
    if (!slots.length) {
      setFeePreview({});
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      previewDeliveryFeeWaivers({
        address: delivery.address,
        address2: delivery.address2,
        city: delivery.city,
        state: delivery.state,
        zip: delivery.zip,
        slots,
      }).then((result) => {
        if (cancelled || !result) return;
        const byKey = {};
        result.forEach((s) => {
          byKey[slotKey(s.day, s.time)] = { fee: s.fee, waived: s.waived };
        });
        setFeePreview(byKey);
      });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    zipStatus.state,
    zipStatus.slots,
    cart,
    delivery.address,
    delivery.address2,
    delivery.city,
    delivery.state,
    delivery.zip,
  ]);

  if (!cart.length) {
    return <Navigate to="/this-weeks-menu" replace />;
  }

  const taxRate = zipStatus.state === 'supported' ? zipStatus.taxRate : null;
  const tax = taxRate != null ? cartSubtotal * taxRate : null;
  // One entry per delivery date. Each date is charged once, so the fee is the
  // sum across groups rather than per meal.
  const deliveryGroups = groupCartByDelivery(cart, zipStatus.slots, delivery);
  const deliveryFeeBeforeWaivers = cartDeliveryFee(cart, zipStatus.slots, delivery);
  // Preview only, from the feePreview effect above — checkout re-checks authoritatively.
  const waivedAmount = deliveryGroups.reduce(
    (sum, g) => sum + (feePreview[g.key]?.waived && g.fee != null ? g.fee : 0),
    0,
  );
  const deliveryFee = deliveryFeeBeforeWaivers != null
    ? Math.max(0, deliveryFeeBeforeWaivers - waivedAmount)
    : null;
  const preSurchargeTotal = cartSubtotal + (tax ?? 0) + (deliveryFee ?? 0);
  const surcharge = preSurchargeTotal * CARD_FEE_RATE;
  const total = preSurchargeTotal + surcharge;
  const zipBlocked = zipStatus.state === 'unsupported';
  const noSlotsForZip = zipStatus.state === 'supported' && zipStatus.slots.length === 0;
  const slotRequiredButMissing =
    zipStatus.state === 'supported' && zipStatus.slots.length > 0 && deliveryFee == null;
  // Everything checkout needs to place the order: a deliverable address
  // (deliveryFee only resolves once the zip is supported and every meal's
  // slot has a priced fee — covers zipBlocked/noSlotsForZip/slotRequiredButMissing
  // and an address that's simply blank so far) plus the two fields onSubmit
  // itself validates. Greys out the button instead of letting the click
  // through to a form error.
  const canSubmit =
    deliveryFee != null &&
    Boolean(String(delivery.recipientName || '').trim()) &&
    isValidPhone(delivery.phone);

  // Stands in for the picker until the address resolves — an empty dropdown is
  // worse than a sentence.
  const deliveryPlaceholder = zipBlocked
    ? 'Delivery unavailable for this address'
    : zipStatus.state === 'checking'
      ? 'Checking your area…'
      : 'Enter your address to choose a delivery time';

  // Peels one serving off a row onto a different day.
  const splitGroupDelivery = (group) => {
    const donor = group.lines[group.lines.length - 1];
    const nextSlot = sortedSlots.find((s) => slotKey(s.day, s.time) !== donor.deliverySlot);
    if (!nextSlot) return;
    splitCartLineServing(donor.lineId, nextSlot);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (zipBlocked || slotRequiredButMissing) return;
    if (!String(delivery.recipientName || '').trim()) {
      setError('Please enter a delivery name so we know who the order is for.');
      return;
    }
    if (!isValidPhone(delivery.phone)) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await submitGuestCheckout({
        entryFlow,
        hasFullQuiz: primaryHasFullQuiz,
        cart,
        householdMembers,
        delivery,
      });
      if (result.error === 'unsupported_zip') {
        setZipStatus({ state: 'unsupported', taxRate: null, message: result.message, slots: [] });
        setBusy(false);
        return;
      }
      if (result.error === 'invalid_delivery_slot') {
        setError(result.message || 'That delivery day/time is no longer available. Please pick another.');
        setBusy(false);
        return;
      }
      if (!result.success) {
        setError('Checkout failed. Please try again.');
        setBusy(false);
        return;
      }
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      setLastOrder(result.orderId);
      navigate(`/confirmation?order=${result.orderId}`, {
        state: { showOptionalQuiz: result.showOptionalQuiz },
      });
    } catch {
      setError('Checkout failed. Please try again.');
      setBusy(false);
    }
  };

  return (
    <main className="sf-page sf-checkout">
      <form className="sf-page__inner sf-checkout__grid" onSubmit={onSubmit}>
        <div>
          <h1 className="sf-page__title">Checkout</h1>

          <section className="sf-checkout__section">
            <h2>1. Deliver to</h2>
            <div className="sf-checkout__section--card">
              <DeliveryAddressFields value={delivery} onChange={setDelivery} idPrefix="checkout">
                {/* Guest checkout has no account, so we need a name to label the
                    delivery for the driver (Claire, Jul 2026). */}
                <input
                  className="sf-input"
                  placeholder="Name"
                  value={delivery.recipientName || ''}
                  onChange={(e) => setDelivery({ recipientName: e.target.value })}
                  required
                  autoComplete="name"
                  data-testid="checkout-recipient-name"
                />
              </DeliveryAddressFields>
            </div>

            {zipBlocked && (
              <p className="sf-error sf-checkout__zip-error">{zipStatus.message}</p>
            )}
            {noSlotsForZip && (
              <p className="sf-error sf-checkout__zip-error">
                No delivery slots are configured for your area yet. Please check back soon.
              </p>
            )}
          </section>

          <section className="sf-checkout__section">
            <h2>2. Your meals & when they arrive</h2>

            <div className="sf-checkout__schedule">
              <div className="sf-checkout__schedule-row">
                <label className="sf-checkout__schedule-label" htmlFor="checkout-consolidated-slot">
                  Deliver everything on
                </label>
                {sortedSlots.length > 0 ? (
                  <DeliverySlotSelect
                    id="checkout-consolidated-slot"
                    className="sf-checkout__schedule-select"
                    slots={sortedSlots}
                    value={cart[0]?.deliverySlot || preferredSlotKey(sortedSlots)}
                    disabled={splitDelivery}
                    onChange={(slot) => { if (slot) setCartDeliverySlots(slot, { validSlots: [slot] }); }}
                    data-testid={splitDelivery ? undefined : 'meal-delivery-slot'}
                  />
                ) : (
                  <span className="sf-checkout__schedule-placeholder">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 3" />
                    </svg>
                    {deliveryPlaceholder}
                  </span>
                )}
              </div>
              {sortedSlots.length > 1 && (
                <label className="sf-checkout__schedule-split">
                  <input
                    type="checkbox"
                    checked={splitDelivery}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        const preferred = preferredSlot(sortedSlots);
                        if (preferred) {
                          setCartDeliverySlots(preferred, { validSlots: [preferred] });
                        }
                      }
                      setSplitDelivery(e.target.checked);
                    }}
                  />
                  Split into separate deliveries (meals on different days)
                </label>
              )}
              {splitDelivery && sortedSlots.length > 0 && (
                <p className="sf-checkout__delivery-hint">
                  Choose a delivery time for each meal below.
                </p>
              )}
            </div>


            {cartByMember.map(({ member, groupedLines }) => (
              <div key={member.id} className="sf-checkout-member-group">
                <h3 className="sf-checkout-member-group__name">{memberDisplayName(member)}</h3>
                <ul className="sf-checkout-cart">
                  {groupedLines.map((group) => {
                    const firstLine = group.lines[0];
                    const line = firstLine;
                    const lastLine = group.lines[group.lines.length - 1];
                    return (
                      <li key={group.key} className="sf-checkout-cart__line">
                        {/* Dish, when it arrives, and what it costs — the three
                            things being decided here. The delivery fee is per
                            date across the order, not per meal, so it lives in
                            the summary rather than on this row. */}
                        <div className="sf-checkout-cart__top">
                          <span className="sf-checkout-cart__name">{line.name}</span>
                          <span className="sf-checkout-cart__price">
                            ${(getLineUnitPrice(line) * group.quantity).toFixed(2)}
                          </span>
                        </div>
                        {splitDelivery && sortedSlots.length > 0 && (
                          <div className="sf-checkout-cart__delivery">
                            <svg
                              className="sf-checkout-cart__delivery-icon"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <rect x="1" y="3" width="15" height="13" />
                              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                              <circle cx="5.5" cy="18.5" r="2.5" />
                              <circle cx="18.5" cy="18.5" r="2.5" />
                            </svg>
                            {/* Grouped so a narrow column wraps the pair, not just the select. */}
                            <span className="sf-checkout-cart__delivery-field">
                              <label
                                className="sf-checkout-cart__delivery-label"
                                htmlFor={`delivery-slot-${group.key}`}
                              >
                                Delivered on:
                              </label>
                              <DeliverySlotSelect
                                id={`delivery-slot-${group.key}`}
                                className="sf-checkout-cart__delivery-select"
                                slots={sortedSlots}
                                value={firstLine.deliverySlot || ''}
                                onChange={(slot) => { if (slot) group.lines.forEach((l) => setCartLineDeliverySlot(l.lineId, slot)); }}
                                data-testid="meal-delivery-slot"
                              />
                            </span>
                            {group.quantity > 1 && sortedSlots.length > 1 && (
                              <button
                                type="button"
                                className="sf-checkout-cart__delivery-split"
                                onClick={() => splitGroupDelivery(group)}
                              >
                                Split Meals
                              </button>
                            )}
                          </div>
                        )}
                        <span className="sf-checkout-cart__meta">{line.mealType}</span>
                        {/* Upcharges sit on the ingredient that carries them, so
                            it's obvious which choice costs extra. */}
                        <div className="sf-checkout-cart__ingredients">
                          {summarizeSelectedIngredients(line).map(({ label, items }) => (
                            <div key={label} className="sf-checkout-cart__ingredient-row">
                              <span className="sf-checkout-cart__ingredient-label">{label}:</span>{' '}
                              {items.map((item, i) => (
                                <React.Fragment key={item.name}>
                                  {i > 0 && ', '}
                                  {item.name}
                                  {item.surcharge > 0 && (
                                    <span className="sf-checkout-cart__surcharge">
                                      {' '}(+${item.surcharge.toFixed(2)})
                                    </span>
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                          ))}
                        </div>

                        {/* Quantity is editable here so people don't have to go
                            back to the menu to change how many meals they want. */}
                        <div className="sf-checkout-cart__actions">
                          <button
                            type="button"
                            className="sf-checkout-cart__remove"
                            onClick={() => group.lines.forEach((l) => removeFromCart(l.lineId))}
                          >
                            Remove
                          </button>
                          <div className="sf-checkout-qty">
                            <button
                              type="button"
                              className="sf-checkout-qty__btn"
                              aria-label={`Decrease quantity of ${line.name}`}
                              onClick={() => setCartLineQuantity(lastLine.lineId, (lastLine.quantity || 1) - 1)}
                            >
                              −
                            </button>
                            <span className="sf-checkout-qty__value" aria-live="polite">{group.quantity}</span>
                            <button
                              type="button"
                              className="sf-checkout-qty__btn"
                              aria-label={`Increase quantity of ${line.name}`}
                              onClick={() => setCartLineQuantity(firstLine.lineId, (firstLine.quantity || 1) + 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
            <Link to="/this-weeks-menu" className="sf-link">
              ← Edit Meals
            </Link>

            <div className="sf-checkout__notes">
              <label className="sf-checkout__notes-label" htmlFor="checkout-order-notes">
                Meal notes / Additional info
              </label>
              <textarea
                id="checkout-order-notes"
                className="sf-input sf-textarea"
                style={{ fontSize: '0.9rem' }}
                placeholder="Anything else we should know — plus the name of your referring nutritionist or fitness trainer, if applicable."
                value={delivery.orderNotes || ''}
                onChange={(e) => setDelivery({ orderNotes: e.target.value })}
                rows={3}
                maxLength={500}
                data-testid="checkout-order-notes"
              />
            </div>
          </section>

          {error && <p className="sf-error">{error}</p>}
        </div>

        <aside className="sf-checkout-summary">
          <h2>Order Summary</h2>

          {/* Per date, so it's clear what each extra date costs. Held back until
              the address resolves — before that every meal is in one
              unscheduled group, which says nothing. */}
          {sortedSlots.length > 0 && deliveryGroups.map((group) => (
            <div key={group.key} className="sf-checkout-summary__group">
              <div className="sf-checkout-summary__group-head">
                <span className="sf-checkout-summary__group-label">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="1" y="3" width="15" height="13" />
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                    <circle cx="5.5" cy="18.5" r="2.5" />
                    <circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                  {group.label || 'Delivery time not selected'}
                </span>
                <span className="sf-checkout-summary__group-fee">
                  {group.fee == null ? (
                    <span className="sf-checkout-summary__tax-pending">Pending</span>
                  ) : feePreview[group.key]?.waived ? (
                    <span className="sf-checkout-summary__fee-waived">
                      <s>${group.fee.toFixed(2)}</s> $0.00{' '}
                      <span
                        className="sf-badge-waived"
                        tabIndex={0}
                        title="Another order is already going to this address for this delivery date and time, so we've waived this delivery fee."
                      >
                        Waived
                      </span>
                    </span>
                  ) : (
                    `$${group.fee.toFixed(2)}`
                  )}
                </span>
              </div>
              <ul className="sf-checkout-summary__group-meals">
                {groupCartLinesBySignature(group.lines).map((entry) => {
                  const firstLine = entry.lines[0];
                  const member = householdMembers.find((m) => m.id === firstLine.memberId);
                  const activeProtein = firstLine.proteinOptions?.find((p) => p.isActive);
                  const isPremiumProtein = Boolean(activeProtein?.premium);
                  const ingredients = summarizeSelectedIngredients(firstLine).flatMap((cat) =>
                    cat.items.map((item) => ({ ...item, category: cat.label })),
                  );
                  return (
                    <li key={entry.key} className="sf-checkout-summary__meal">
                      <div className="sf-checkout-summary__meal-top">
                        <span className="sf-checkout-summary__meal-name">
                          {firstLine.name}
                          {entry.quantity > 1 && ` ×${entry.quantity}`}
                        </span>
                        <span className="sf-checkout-summary__meal-price">
                          ${(getLineUnitPrice(firstLine) * entry.quantity).toFixed(2)}
                        </span>
                      </div>
                      <div className="sf-checkout-summary__meal-meta">
                        {firstLine.mealType}
                        {member && ` · ${memberDisplayName(member)}`}
                      </div>
                      {/* {ingredients.length > 0 && (
                        <div className="sf-checkout-summary__meal-ingredients">
                          {ingredients.map((item, i) => (
                            <React.Fragment key={item.name}>
                              {i > 0 && ', '}
                              {item.category === 'Protein' && isPremiumProtein ? (
                                <span className="sf-checkout-summary__meal-premium">{item.name} · Premium</span>
                              ) : (
                                item.name
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )} */}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          <div className="sf-checkout-summary__totals">
            <div className="sf-checkout-summary__row">
              <span>Subtotal</span>
              <span>${cartSubtotal.toFixed(2)}</span>
            </div>

            <div className="sf-checkout-summary__row">
              <span>Delivery{deliveryGroups.length > 1 ? ` (${deliveryGroups.length} dates)` : ''}</span>
              {deliveryFee == null ? (
                <span className="sf-checkout-summary__tax-pending">Select delivery options</span>
              ) : waivedAmount > 0 ? (
                <span className="sf-checkout-summary__fee-waived">
                  <s>${deliveryFeeBeforeWaivers.toFixed(2)}</s> ${deliveryFee.toFixed(2)}{' '}
                </span>
              ) : (
                <span className="sf-checkout-summary__tax-pending">${deliveryFee.toFixed(2)}</span>
              )}
            </div>

            <div className="sf-checkout-summary__row">
              {zipStatus.state === 'supported' ? (
                <>
                  <span>Tax ({formatTaxRatePercent(taxRate)}%)</span>
                  <span className="sf-checkout-summary__tax-pending">${tax.toFixed(2)}</span>
                </>
              ) : zipStatus.state === 'checking' ? (
                <>
                  <span>Tax</span>
                  <span className="sf-checkout-summary__tax-pending">Checking your area…</span>
                </>
              ) : zipBlocked ? (
                <>
                  <span>Tax</span>
                  <span className="sf-checkout-summary__tax-pending">Unavailable</span>
                </>
              ) : (
                <>
                  <span>Tax</span>
                  <span className="sf-checkout-summary__tax-pending">Enter address to calculate</span>
                </>
              )}
            </div>

            <div className="sf-checkout-summary__row">
              <span>Card Processing Fee (2.9%)</span>
              <span className="sf-checkout-summary__tax-pending">{deliveryFee != null ? `$${surcharge.toFixed(2)}` : 'Pending'}</span>
            </div>
            <div className="sf-checkout-summary__row sf-checkout-summary__total">
              <span>Total</span>
              <span>
                {taxRate != null && deliveryFee != null
                  ? `$${total.toFixed(2)}`
                  : `~$${(cartSubtotal + (deliveryFee ?? 0)).toFixed(2)}+tax+fee`}
              </span>
            </div>
          </div>
          <button
            type="submit"
            className="sf-btn-flow sf-btn-flow--primary sf-btn-flow--block"
            disabled={busy || !canSubmit}
            data-testid="checkout-submit"
          >
            {busy ? 'Redirecting to payment…' : 'Continue to Payment'}
          </button>

          <p className="sf-page__meta sf-checkout__stripe-note">
            You&apos;ll enter your email, discount code, and payment details on the next step, powered by Stripe.
          </p>
        </aside>
      </form>
    </main>
  );
}
